import { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControlLabel, InputAdornment,
  Paper, Stack, TextField, Typography,
} from "@mui/material";
import {
  createSettlementLine, getSettlementFeatures, getSettlementLines,
  updateSettlementAmount, updateSettlementFlags,
  type SettlementFeatures, type SettlementLine, type SettlementType,
} from "../../api/settlementLines";
import { formatCurrencyAmount } from "../../utils/formatters";
import { sumSettlementAmounts } from "../../utils/settlementAmounts";
import { parseSettlementMagnitude, planSettlementChange } from "../../utils/settlementSingleLine";

type Props = {
  documentId: string;
  companyId: string;
  currency: string;
  canEditDocument: boolean;
  onUpdated: () => void;
  onBusyChange: (busy: boolean) => void;
  onCancel: () => void;
  onSaved: () => void;
};

type PendingCreate = Partial<Record<SettlementType, number>>;
const kinds: SettlementType[] = ["Charge", "Deduction"];
const nameOf = (kind: SettlementType) => kind === "Charge" ? "Obciążenia" : "Odliczenia";

export default function DocumentSettlementSection({
  documentId, companyId, currency, canEditDocument,
  onUpdated, onBusyChange, onCancel, onSaved,
}: Props) {
  const [features, setFeatures] = useState<SettlementFeatures | null>(null);
  const [originals, setOriginals] = useState<SettlementLine[]>([]);
  const [lines, setLines] = useState<SettlementLine[]>([]);
  const [pendingCreate, setPendingCreate] = useState<PendingCreate>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editor, setEditor] = useState<SettlementType | null>(null);
  const [amount, setAmount] = useState("");
  const [pendingSkip, setPendingSkip] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const settings = await getSettlementFeatures(companyId);
        if (!active) return;
        setFeatures(settings);
        if (!settings.useKSeFSettlementHandling) return;
        const values = await getSettlementLines(companyId, documentId);
        if (active) {
          setOriginals(values);
          setLines(values.map((value) => ({ ...value })));
          setPendingCreate({});
        }
      } catch (e) {
        if (active) setError(e instanceof Error ? e.message : "Nie udało się pobrać rozliczenia.");
      } finally {
        if (active) setLoading(false);
      }
    };
    void load();
    return () => { active = false; };
  }, [companyId, documentId]);

  const changedLines = useMemo(() => {
    const lookup = new Map(originals.map((line) => [line.systemId, line]));
    return lines.filter((line) => {
      const initial = lookup.get(line.systemId);
      return initial && (initial.directUnitCost !== line.directUnitCost ||
        initial.skipInPosting !== line.skipInPosting ||
        initial.skipInSettlement !== line.skipInSettlement);
    });
  }, [originals, lines]);

  const hasChanges = changedLines.length > 0 || kinds.some((kind) => pendingCreate[kind] !== undefined);

  const openAmountEditor = (kind: SettlementType) => {
    const row = lines.find((line) => line.settlementType === kind);
    const source = originals.find((line) => line.systemId === row?.systemId);
    const current = pendingCreate[kind] ?? (row
      ? source?.directUnitCost !== row.directUnitCost
        ? row.directUnitCost : row.amount ?? row.directUnitCost
      : null);
    setAmount(current == null ? "" : String(Math.abs(current)));
    setEditor(kind);
    setError(null);
  };

  const amountValidationError = useMemo(() => {
    if (!editor) return null;
    try {
      const value = parseSettlementMagnitude(editor, amount);
      planSettlementChange(editor, lines.filter((line) => line.settlementType === editor),
        value, Boolean(features?.allowManualSettlementLine && canEditDocument));
      return null;
    } catch (e) {
      return e instanceof Error ? e.message : "Nieprawidłowa kwota.";
    }
  }, [editor, amount, lines, features, canEditDocument]);

  const acceptAmount = () => {
    if (!editor || amountValidationError) return;
    try {
      const value = parseSettlementMagnitude(editor, amount);
      const plan = planSettlementChange(editor,
        lines.filter((line) => line.settlementType === editor), value,
        Boolean(features?.allowManualSettlementLine && canEditDocument));
      if (plan.action === "create") {
        setPendingCreate((previous) => ({ ...previous, [editor]: plan.amount }));
      } else {
        setLines((previous) => previous.map((line) => line.systemId === plan.systemId
          ? { ...line, directUnitCost: plan.directUnitCost } : line));
      }
      setEditor(null);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Nie można zmienić kwoty.");
    }
  };

  const setFlag = (lineId: string, property: "skipInPosting" | "skipInSettlement", checked: boolean) => {
    setLines((previous) => previous.map((line) => line.systemId === lineId
      ? { ...line, [property]: checked } : line));
  };

  const saveAll = async () => {
    if (busy || loading || refreshFailed || editor || pendingSkip) return;
    if (!hasChanges) { onCancel(); return; }
    const lookup = new Map(originals.map((line) => [line.systemId, line]));
    try {
      // Validate all operations before sending the first request to BC.
      for (const kind of kinds) {
        const same = lines.filter((line) => line.settlementType === kind);
        const updated = changedLines.find((line) => line.settlementType === kind &&
          lookup.get(line.systemId)?.directUnitCost !== line.directUnitCost);
        if (updated) planSettlementChange(kind, same, Number(updated.directUnitCost), false);
        if (pendingCreate[kind] !== undefined) {
          const plan = planSettlementChange(kind, same, pendingCreate[kind]!,
            Boolean(features?.allowManualSettlementLine && canEditDocument));
          if (plan.action !== "create") throw new Error("Nie można dodać duplikatu wiersza.");
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Nieprawidłowe zmiany.");
      return;
    }

    setBusy(true);
    onBusyChange(true);
    setError(null);
    let completed = 0;
    try {
      for (const line of changedLines) {
        const initial = lookup.get(line.systemId);
        if (!initial) continue;
        if (initial.directUnitCost !== line.directUnitCost) {
          await updateSettlementAmount(companyId, documentId, line.systemId, Number(line.directUnitCost));
          completed += 1;
        }
        const flags: { skipInPosting?: boolean; skipInSettlement?: boolean } = {};
        if (initial.skipInPosting !== line.skipInPosting) flags.skipInPosting = line.skipInPosting;
        if (initial.skipInSettlement !== line.skipInSettlement) flags.skipInSettlement = line.skipInSettlement;
        if (Object.keys(flags).length) {
          await updateSettlementFlags(companyId, documentId, line.systemId, flags);
          completed += 1;
        }
      }
      for (const kind of kinds) {
        const value = pendingCreate[kind];
        if (value !== undefined) {
          await createSettlementLine(companyId, documentId, kind, value);
          completed += 1;
        }
      }
      onSaved();
    } catch (e) {
      const reason = e instanceof Error ? e.message : "Błąd zapisu.";
      // A failed HTTP response does not prove BC rolled back the request.
      // Always reload to prevent accidental repeat POST and double booking.
      onUpdated();
      try {
        const fresh = await getSettlementLines(companyId, documentId);
        setOriginals(fresh);
        setLines(fresh.map((line) => ({ ...line })));
        setPendingCreate({});
        setError(`${completed ? "Część zmian mogła zostać zapisana. " : "Zapis nie został ukończony. "}${reason} ` +
          "Dane odczytano ponownie z BC; sprawdź je przed ponownym zapisem.");
      } catch {
        setRefreshFailed(true);
        setError(`Nie udało się potwierdzić zapisu: ${reason} Zamknij okno, aby ponownie wczytać dokument.`);
      }
    } finally {
      setBusy(false);
      onBusyChange(false);
    }
  };

  if (loading) return (
    <Paper sx={{ p: 2 }}><CircularProgress size={20} /> Pobieranie rozliczenia KSeF...
      <DialogActions><Button onClick={onCancel}>Anuluj</Button></DialogActions>
    </Paper>
  );

  if (!features?.useKSeFSettlementHandling) return (
    <Paper sx={{ p: 2 }}>
      {error ? <Alert severity="error">{error}</Alert>
        : <Typography>Obsługa obciążeń i odliczeń jest wyłączona w BC.</Typography>}
      <DialogActions><Button onClick={onCancel}>Zamknij</Button></DialogActions>
    </Paper>
  );

  const settlementRows = lines.filter((line) =>
    line.settlementType === "Charge" || line.settlementType === "Deduction");

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>Obciążenia i odliczenia KSeF</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Wiersze rozliczeń mają stawkę NP. Wymiary są obsługiwane przez BC na podstawie nagłówka dokumentu.
        Zmiany zostaną przesłane dopiero po kliknięciu „Zapisz”.
      </Typography>
      {hasChanges && <Alert severity="warning" sx={{ mb: 2 }}>Masz niezapisane zmiany.</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Stack spacing={2}>
        {kinds.map((kind) => {
          const matching = settlementRows.filter((line) => line.settlementType === kind);
          const row = matching.length === 1 ? matching[0] : undefined;
          const original = originals.find((line) => line.systemId === row?.systemId);
          const addedAmount = pendingCreate[kind];
          const total = addedAmount !== undefined ? addedAmount
            : row && original?.directUnitCost !== row.directUnitCost
              ? Number(row.directUnitCost)
              : sumSettlementAmounts(matching, originals);
          const canEditAmount = canEditDocument && !refreshFailed &&
            (row ? row.settlementAmountEditable && !row.lineMatched &&
              Number(row.quantity ?? 1) === 1
              : matching.length === 0 && features.allowManualSettlementLine);
          return (
            <Box key={kind} sx={{ border: 1, borderColor: "divider", borderRadius: 1.5, p: 1.5 }}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}
                justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }}>
                <Box>
                  <Typography variant="subtitle2">{nameOf(kind)} — NP</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatCurrencyAmount(total)} {currency}
                    {(addedAmount !== undefined || (row && original?.directUnitCost !== row.directUnitCost))
                      ? " (niezapisane)" : ""}
                  </Typography>
                  {!matching.length && addedAmount === undefined && (
                    <Typography variant="caption" color="text.secondary">Brak wiersza tego typu.</Typography>
                  )}
                </Box>
                {canEditAmount && <Button size="small" variant="outlined" disabled={busy}
                  onClick={() => openAmountEditor(kind)}>
                  {matching.length ? "Zmień kwotę" : "Dodaj"}
                </Button>}
              </Stack>
              {matching.length > 1 && <Alert severity="warning" sx={{ mt: 1 }}>
                Kilka wierszy tego typu — ich edycja wymaga uporządkowania danych w BC.
              </Alert>}
              {row && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0, sm: 2 }}>
                    {(row.skipInPostingAllowed || row.skipInPosting) && (
                      <FormControlLabel label="Pomiń w księgowaniu" control={
                        <Checkbox size="small" checked={row.skipInPosting}
                          disabled={busy || refreshFailed || !canEditDocument ||
                            (!row.skipInPostingAllowed && !row.skipInPosting)}
                          onChange={(_, checked) => checked
                            ? setPendingSkip(row.systemId)
                            : setFlag(row.systemId, "skipInPosting", false)} />
                      } />
                    )}
                    {(row.lineEditable || row.skipInSettlement) && (
                      <FormControlLabel label="Pomiń w rozrachunkach" control={
                        <Checkbox size="small" checked={row.skipInSettlement}
                          disabled={busy || refreshFailed || !canEditDocument ||
                            (!row.lineEditable && !row.skipInSettlement)}
                          onChange={(_, checked) => setFlag(row.systemId, "skipInSettlement", checked)} />
                      } />
                    )}
                  </Stack>
                  {row.skipInPosting && row.skipInSettlement && <Alert severity="info" sx={{ mt: 1 }}>
                    Według reguł BC pierwszeństwo ma pominięcie w rozrachunkach.
                  </Alert>}
                </>
              )}
            </Box>
          );
        })}
      </Stack>
      <DialogActions sx={{ px: 0, pb: 0, mt: 2 }}>
        <Button disabled={busy} onClick={onCancel}>Anuluj</Button>
        <Button variant="contained" disabled={busy || refreshFailed || !hasChanges || !canEditDocument}
          startIcon={busy ? <CircularProgress size={16} /> : undefined}
          onClick={() => void saveAll()}>Zapisz</Button>
      </DialogActions>
      <Dialog open={Boolean(editor)} onClose={busy ? undefined : () => setEditor(null)} fullWidth maxWidth="xs">
        <DialogTitle>{editor && `${nameOf(editor)} — kwota NP`}</DialogTitle>
        <DialogContent>
          <TextField fullWidth autoFocus margin="normal" size="small" label="Kwota"
            type="number" value={amount} disabled={busy}
            inputProps={{ min: 0, step: "0.01" }}
            InputProps={editor === "Deduction" ? {
              startAdornment: <InputAdornment position="start">−</InputAdornment>,
            } : undefined}
            onChange={(e) => setAmount(e.target.value)}
            error={Boolean(amount.trim() && amountValidationError)}
            helperText={amount.trim() && amountValidationError
              ? amountValidationError : "Wpisz dodatnią wartość; odliczenie otrzyma znak minus."} />
          <Alert severity="info" sx={{ mt: 2 }}>
            {editor === "Deduction" ? "Odliczenie zostanie zapisane w BC jako kwota ujemna."
              : "Obciążenie zostanie zapisane w BC jako kwota dodatnia."}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditor(null)} disabled={busy}>Anuluj</Button>
          <Button variant="contained" onClick={acceptAmount}
            disabled={busy || Boolean(amountValidationError)}>Zatwierdź kwotę</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(pendingSkip)} onClose={busy ? undefined : () => setPendingSkip(null)}>
        <DialogTitle>Pomiń w księgowaniu?</DialogTitle>
        <DialogContent><Typography variant="body2">
          Po zatwierdzeniu BC może nieodwracalnie usunąć ten wiersz wraz z jego wymiarami,
          chyba że wybrano również pominięcie w rozrachunkach.
        </Typography></DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setPendingSkip(null)}>Anuluj</Button>
          <Button disabled={busy} variant="contained" color="warning" onClick={() => {
            if (pendingSkip) setFlag(pendingSkip, "skipInPosting", true);
            setPendingSkip(null);
          }}>Potwierdź</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
